import type { MindMapNode } from '@/model/types';

/**
 * Convert a mind map tree to Markdown.
 *
 * - Root becomes `# Title`
 * - Level 1 nodes become `## Heading`
 * - Level 2 nodes become `### Heading`
 * - Deeper levels become bullet points with indentation
 * - Node notes appear as body text under their heading/bullet
 */
export function exportMarkdown(nodes: Map<string, MindMapNode>, rootNodeId: string): string {
  const lines: string[] = [];
  const root = nodes.get(rootNodeId);
  if (!root) return '';

  walkNode(root, 0, nodes, lines);
  return lines.join('\n') + '\n';
}

function walkNode(
  node: MindMapNode,
  depth: number,
  nodes: Map<string, MindMapNode>,
  lines: string[],
): void {
  if (depth === 0) {
    lines.push(`# ${node.text}`);
  } else if (depth === 1) {
    lines.push('');
    lines.push(`## ${node.text}`);
  } else if (depth === 2) {
    lines.push('');
    lines.push(`### ${node.text}`);
  } else {
    const indent = '  '.repeat(depth - 3);
    lines.push(`${indent}- ${node.text}`);
  }

  if (node.notes) {
    if (depth <= 2) {
      lines.push('');
      lines.push(node.notes);
    } else {
      const indent = '  '.repeat(depth - 3);
      lines.push('');
      // Indent each line of notes to match the bullet
      for (const noteLine of node.notes.split('\n')) {
        lines.push(`${indent}  ${noteLine}`);
      }
    }
  }

  for (const childId of node.childIds) {
    const child = nodes.get(childId);
    if (child) {
      walkNode(child, depth + 1, nodes, lines);
    }
  }
}
